import React, { useEffect, useMemo, useState } from 'react'
import { Cog6ToothIcon } from '@heroicons/react/24/outline'
import { Button } from '../../components/ui/button'
import { getLocalApiUrl, authFetch } from '../../config/api'
import FormFillFields from './FormFillFields'
import FormBrandingHeader, { FormTenantLogo, formSubmitButtonStyle } from './FormBrandingHeader'
import {
  ALWAYS_FULL,
  BG_SWATCHES,
  CATALOGS,
  CHOICE_TYPES,
  DEFAULT_LOCALE,
  FIELD_TYPES,
  FORM_WIDTHS,
  LOCALES,
  NO_CONDITION_SOURCE,
  PLACEMENT_SLOTS,
  TIMEZONES,
  WORKFLOW_ASSIGNEE_TYPES,
  condChipText,
  defaultField,
  defaultWorkflowSteps,
  emptyDefinition,
  flattenFields,
  formCardMaxWidth,
  formPageBackgroundIsDark,
  formPageStyle,
  labelOf,
  localeName,
  normalizeAfterSubmission,
  normalizeAppearance,
  normalizeAvailability,
  normalizeDrafts,
  normalizeLocales,
  normalizePlacements,
  normalizeWorkflowStep,
  operatorNeedsValue,
  opsForSource,
  optionLine,
  pageTitle,
  parseOptionLines,
  setLocalized,
  typeName,
  uid,
} from './formBuilderModel'

const inputCls = 'mt-1 w-full rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm text-slate-900 outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-600/20'
const chipOn = 'border-teal-600 bg-teal-50 text-teal-800'
const chipOff = 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'

export default function FormBuilderWorkspace({
  form,
  definition,
  settings,
  onDefinitionChange,
  onSettingsChange,
  onFormPatch,
  onSave,
  onPublish,
  onTest,
  saving,
}) {
  const [mode, setMode] = useState('build')
  const pages = definition?.pages?.length ? definition.pages : emptyDefinition().pages
  const locales = normalizeLocales(settings)
  const [editLocale, setEditLocale] = useState(DEFAULT_LOCALE)
  const [activePageId, setActivePageId] = useState(pages[0]?.id)
  const [selected, setSelected] = useState({ kind: 'page', id: pages[0]?.id })
  const [previewAnswers, setPreviewAnswers] = useState({})
  const [tenantBranding, setTenantBranding] = useState(null)
  const appearanceForBranding = normalizeAppearance(settings)
  const wantsTenantBranding =
    form?.facing === 'patron' &&
    (appearanceForBranding.showTenantLogo || appearanceForBranding.useClubColors)

  useEffect(() => {
    if (!wantsTenantBranding) {
      setTenantBranding(null)
      return undefined
    }
    let cancelled = false
    authFetch(getLocalApiUrl('/api/forms/tenant-branding'))
      .then((r) => r.json())
      .then((j) => {
        if (!cancelled && j.success) setTenantBranding(j.data || null)
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [wantsTenantBranding])

  useEffect(() => {
    if (!locales.includes(editLocale)) setEditLocale(DEFAULT_LOCALE)
  }, [locales, editLocale])

  const activePage = pages.find((p) => p.id === activePageId) || pages[0]

  useEffect(() => {
    if (!pages.some((p) => p.id === activePageId) && pages[0]) setActivePageId(pages[0].id)
  }, [pages, activePageId])

  const setPages = (fn) => {
    onDefinitionChange((def) => {
      const cur = def?.pages?.length ? def.pages : emptyDefinition().pages
      return { ...def, schemaVersion: 1, pages: fn(cur) }
    })
  }

  const mapSections = (fn) => setPages((prev) => prev.map((p) => ({ ...p, sections: (p.sections || []).map(fn) })))
  const updatePage = (id, patch) => setPages((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)))
  const updateSection = (id, patch) => mapSections((s) => (s.id === id ? { ...s, ...patch } : s))
  const updateField = (id, patch) => mapSections((s) => ({
    ...s,
    fields: (s.fields || []).map((f) => (f.id === id ? { ...f, ...patch } : f)),
  }))

  const stripDangling = (removedIds) => (list) =>
    list.map((p) => ({
      ...p,
      condition: p.condition && removedIds.includes(p.condition.fieldId) ? undefined : p.condition,
      sections: (p.sections || []).map((s) => ({
        ...s,
        condition: s.condition && removedIds.includes(s.condition.fieldId) ? undefined : s.condition,
        fields: (s.fields || []).map((f) => (
          f.condition && removedIds.includes(f.condition.fieldId) ? { ...f, condition: undefined } : f
        )),
      })),
    }))

  const addPage = () => {
    const p = { id: uid('p'), title: `Page ${pages.length + 1}`, sections: [{ id: uid('s'), label: null, columns: 1, fields: [] }] }
    setPages((prev) => [...prev, p])
    setActivePageId(p.id)
    setSelected({ kind: 'page', id: p.id })
  }

  const removePage = (id) => {
    if (pages.length <= 1) return
    const removed = pages.find((p) => p.id === id)?.sections?.flatMap((s) => (s.fields || []).map((f) => f.id)) || []
    setPages((prev) => stripDangling(removed)(prev.filter((p) => p.id !== id)))
    setSelected(null)
  }

  const movePage = (id, dir) => setPages((prev) => {
    const i = prev.findIndex((p) => p.id === id)
    const j = i + dir
    if (j < 0 || j >= prev.length) return prev
    const next = [...prev]
    ;[next[i], next[j]] = [next[j], next[i]]
    return next
  })

  const addSection = () => {
    const s = { id: uid('s'), label: null, columns: 1, fields: [] }
    setPages((prev) => prev.map((p) => (p.id === activePage.id ? { ...p, sections: [...(p.sections || []), s] } : p)))
    setSelected({ kind: 'section', id: s.id })
  }

  const removeSection = (id) => {
    const removed = pages.flatMap((p) => p.sections || []).find((s) => s.id === id)?.fields?.map((f) => f.id) || []
    setPages((prev) => stripDangling(removed)(prev.map((p) => ({ ...p, sections: (p.sections || []).filter((s) => s.id !== id) }))))
    setSelected(null)
  }

  const moveSection = (id, dir) => setPages((prev) => prev.map((p) => {
    const i = (p.sections || []).findIndex((s) => s.id === id)
    if (i === -1) return p
    const j = i + dir
    if (j < 0 || j >= p.sections.length) return p
    const next = [...p.sections]
    ;[next[i], next[j]] = [next[j], next[i]]
    return { ...p, sections: next }
  }))

  const sectionOnActive = (id) => (activePage?.sections || []).some((s) => s.id === id)
  const fieldSectionOnActive = (fid) => (activePage?.sections || []).find((s) => (s.fields || []).some((f) => f.id === fid))
  const targetSection =
    selected?.kind === 'section' && sectionOnActive(selected.id)
      ? activePage.sections.find((s) => s.id === selected.id)
      : selected?.kind === 'field' && fieldSectionOnActive(selected.id)
        ? fieldSectionOnActive(selected.id)
        : (activePage?.sections || []).at(-1)

  const addField = (type) => {
    const f = defaultField(type)
    if (!targetSection) {
      const s = { id: uid('s'), label: null, columns: 1, fields: [f] }
      setPages((prev) => prev.map((p) => (p.id === activePage.id ? { ...p, sections: [...(p.sections || []), s] } : p)))
    } else {
      setPages((prev) => prev.map((p) => ({
        ...p,
        sections: (p.sections || []).map((s) => (s.id === targetSection.id ? { ...s, fields: [...(s.fields || []), f] } : s)),
      })))
    }
    setSelected({ kind: 'field', id: f.id })
  }

  const removeField = (id) => {
    setPages((prev) => stripDangling([id])(prev.map((p) => ({
      ...p,
      sections: (p.sections || []).map((s) => ({ ...s, fields: (s.fields || []).filter((f) => f.id !== id) })),
    }))))
    if (selected?.kind === 'field' && selected.id === id) setSelected(null)
  }

  const moveField = (id, dir) => setPages((prev) => prev.map((p) => ({
    ...p,
    sections: (p.sections || []).map((s) => {
      const i = (s.fields || []).findIndex((f) => f.id === id)
      if (i === -1) return s
      const j = i + dir
      if (j < 0 || j >= s.fields.length) return s
      const next = [...s.fields]
      ;[next[i], next[j]] = [next[j], next[i]]
      return { ...s, fields: next }
    }),
  })))

  const allFields = flattenFields(pages)
  const selField = selected?.kind === 'field' ? allFields.find((f) => f.id === selected.id) : null
  const selSection = selected?.kind === 'section' ? pages.flatMap((p) => p.sections || []).find((s) => s.id === selected.id) : null
  const selPage = selected?.kind === 'page' ? pages.find((p) => p.id === selected.id) : null

  const modes = [
    { id: 'build', label: 'Build' },
    { id: 'workflow', label: settings.workflow?.enabled ? 'Workflow · on' : 'Workflow' },
    { id: 'preview', label: 'Preview' },
  ]

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
        <div className="flex items-center gap-2">
          {modes.map((m) => (
            <button
              key={m.id}
              type="button"
              role="tab"
              aria-selected={mode === m.id}
              onClick={() => setMode(m.id)}
              className={`rounded-full px-3 py-1.5 text-sm font-semibold ${mode === m.id ? 'bg-teal-700 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
            >
              {m.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          {locales.length > 1 ? (
            <select
              className="rounded-lg border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-700"
              value={editLocale}
              onChange={(e) => setEditLocale(e.target.value)}
              aria-label="Edit language"
            >
              {locales.map((code) => (
                <option key={code} value={code}>{localeName(code)}</option>
              ))}
            </select>
          ) : null}
          <Button variant="outline" size="sm" onClick={onSave} disabled={saving}>{saving ? 'Saving…' : 'Save draft'}</Button>
          {onTest ? (
            <Button variant="outline" size="sm" onClick={onTest} disabled={saving}>Test & record</Button>
          ) : null}
          <Button size="sm" onClick={onPublish}>Publish</Button>
        </div>
      </div>

      {mode === 'build' && (
        <div className="grid grid-cols-1 xl:grid-cols-[220px_minmax(0,1fr)_340px] min-h-[560px]">
          <aside className="border-b xl:border-b-0 xl:border-r border-slate-200 p-3 bg-slate-50/60">
            <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1">Add a field</div>
            <p className="text-xs text-slate-500 mb-3">
              Into <span className="font-semibold text-slate-800">{targetSection ? (labelOf(targetSection.label, editLocale) || 'Untitled section') : 'a new section'}</span>
            </p>
            <div className="grid grid-cols-1 gap-1.5">
              {FIELD_TYPES.map((t) => (
                <button
                  key={t.type}
                  type="button"
                  onClick={() => addField(t.type)}
                  className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-left text-sm hover:border-teal-400 hover:bg-teal-50"
                >
                  <span className="w-6 text-center text-xs text-slate-500">{t.icon}</span>
                  {t.name}
                </button>
              ))}
            </div>
            <button type="button" className="mt-3 w-full rounded-lg border border-dashed border-slate-300 px-2 py-1.5 text-sm font-semibold text-teal-800 hover:bg-white" onClick={addSection}>
              + Add section
            </button>
          </aside>

          <main className="p-4 min-w-0">
            <div className="flex flex-wrap items-center gap-1.5 mb-4">
              {pages.map((p, i) => (
                <div
                  key={p.id}
                  className={`inline-flex items-center rounded-full border ${p.id === activePage?.id ? 'border-teal-600 bg-teal-50 text-teal-900' : 'border-slate-200 text-slate-600'}`}
                >
                  <button
                    type="button"
                    onClick={() => { setActivePageId(p.id); setSelected({ kind: 'page', id: p.id }) }}
                    className={`inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 text-sm ${p.id === activePage?.id ? 'font-semibold' : ''}`}
                  >
                    <span className="text-[11px] text-slate-400">{i + 1}</span>
                    {pageTitle(p, i, editLocale)}
                    {p.condition ? <span className="text-[10px] text-amber-700">if</span> : null}
                  </button>
                  <button
                    type="button"
                    aria-label={`Page settings for ${pageTitle(p, i, editLocale)}`}
                    title="Page settings"
                    onClick={() => { setActivePageId(p.id); setSelected({ kind: 'page', id: p.id }) }}
                    className={`pr-2 py-1 ${selected?.kind === 'page' && selected.id === p.id ? 'text-teal-800' : 'text-slate-400 hover:text-slate-700'}`}
                  >
                    <Cog6ToothIcon className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              <button type="button" className="rounded-full border border-dashed border-slate-300 px-2.5 py-1 text-sm text-slate-500" onClick={addPage}>+</button>
              <button
                type="button"
                className={`ml-auto rounded-full border px-2.5 py-1 text-xs font-semibold ${selected?.kind === 'form' ? chipOn : chipOff}`}
                onClick={() => setSelected({ kind: 'form' })}
              >
                Form settings
              </button>
            </div>

            <div className="flex items-baseline justify-between gap-3 mb-3">
              <h2 className="text-base font-semibold text-slate-900">{pageTitle(activePage, pages.indexOf(activePage), editLocale)}</h2>
              <span className="text-xs text-slate-500">
                {(activePage?.sections || []).length} sections · {(activePage?.sections || []).reduce((n, s) => n + (s.fields || []).length, 0)} fields
              </span>
            </div>

            {!(activePage?.sections || []).length ? (
              <div className="rounded-lg border border-dashed border-slate-300 px-4 py-10 text-center text-sm text-slate-500">This page is empty. Add a section, or add a field to create one.</div>
            ) : null}

            {(activePage?.sections || []).map((s, si) => {
              const chip = s.condition && condChipText(s.condition, pages)
              const cols = s.columns === 3 ? 3 : s.columns === 2 ? 2 : 1
              return (
                <div
                  key={s.id}
                  className={`mb-3 rounded-xl border p-3 ${selSection?.id === s.id ? 'border-teal-500 bg-teal-50/40' : 'border-slate-200 bg-white'}`}
                  onClick={() => setSelected({ kind: 'section', id: s.id })}
                >
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex flex-wrap items-center gap-2 min-w-0">
                      <span className="text-sm font-semibold text-slate-800">{labelOf(s.label, editLocale) || <em className="not-italic text-slate-400">Untitled section</em>}</span>
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-500">{s.columns || 1} col</span>
                      {chip ? <span className="text-[11px] text-amber-800">{chip}</span> : null}
                    </div>
                    <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                      <ToolBtn label="Move section up" onClick={() => moveSection(s.id, -1)} disabled={si === 0}>↑</ToolBtn>
                      <ToolBtn label="Move section down" onClick={() => moveSection(s.id, 1)} disabled={si === (activePage.sections.length - 1)}>↓</ToolBtn>
                      <ToolBtn label="Delete section" danger onClick={() => removeSection(s.id)}>✕</ToolBtn>
                    </div>
                  </div>
                  <div className={`grid gap-2 ${cols === 3 ? 'sm:grid-cols-3' : cols === 2 ? 'sm:grid-cols-2' : 'grid-cols-1'}`}>
                    {!(s.fields || []).length ? (
                      <div className="sm:col-span-full rounded-lg border border-dashed border-slate-200 px-3 py-4 text-xs text-slate-400">Empty — select this section and add fields.</div>
                    ) : null}
                    {(s.fields || []).map((f, fi) => {
                      const full = f.span === 'full' || ALWAYS_FULL.includes(f.type)
                      const fchip = f.condition && condChipText(f.condition, pages)
                      return (
                        <div
                          key={f.id}
                          className={`rounded-lg border bg-white px-3 py-2 ${selField?.id === f.id ? 'border-teal-600 ring-2 ring-teal-600/20' : 'border-slate-200'} ${full && cols > 1 ? 'sm:col-span-full' : ''}`}
                          onClick={(e) => { e.stopPropagation(); setSelected({ kind: 'field', id: f.id }) }}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <div className="text-sm font-semibold text-slate-900 truncate">
                                {labelOf(f.label, editLocale) || 'Untitled'}
                                {f.required ? <span className="text-red-600"> *</span> : null}
                              </div>
                              <div className="mt-0.5 flex flex-wrap gap-1.5 text-[11px] text-slate-500">
                                <span>{typeName(f.type)}</span>
                                {f.catalog ? <span>· {f.catalog}</span> : null}
                                {full && cols > 1 ? <span>· full row</span> : null}
                                {fchip ? <span className="text-amber-800">{fchip}</span> : null}
                              </div>
                            </div>
                            <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                              <ToolBtn label="Move field up" onClick={() => moveField(f.id, -1)} disabled={fi === 0}>↑</ToolBtn>
                              <ToolBtn label="Move field down" onClick={() => moveField(f.id, 1)} disabled={fi === s.fields.length - 1}>↓</ToolBtn>
                              <ToolBtn label="Delete field" danger onClick={() => removeField(f.id)}>✕</ToolBtn>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </main>

          <aside className="border-t xl:border-t-0 xl:border-l border-slate-200 p-3 bg-slate-50/40 xl:overflow-y-auto xl:max-h-[calc(100vh-10rem)]">
            <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-3">Properties</div>
            {selected?.kind === 'form' ? (
              <FormProps form={form} settings={settings} locale={editLocale} onFormPatch={onFormPatch} onSettingsChange={onSettingsChange} />
            ) : null}
            {selPage ? <PageProps page={selPage} pages={pages} locale={editLocale} update={updatePage} remove={removePage} move={movePage} /> : null}
            {selSection ? <SectionProps section={selSection} pages={pages} locale={editLocale} update={updateSection} /> : null}
            {selField ? <FieldProps field={selField} pages={pages} locale={editLocale} update={updateField} /> : null}
            {selected?.kind !== 'form' && !selPage && !selSection && !selField ? (
              <p className="text-sm text-slate-500">Select a page, section, or field to edit it.</p>
            ) : null}
          </aside>
        </div>
      )}

      {mode === 'workflow' && (
        <WorkflowPanel pages={pages} settings={settings} onSettingsChange={onSettingsChange} />
      )}

      {mode === 'preview' && (
        <div className="p-6" style={formPageStyle(settings, tenantBranding)}>
          <div className="mb-4 flex flex-wrap items-start justify-between gap-3 mx-auto" style={{ maxWidth: formCardMaxWidth(settings) }}>
            <p className={`text-xs ${formPageBackgroundIsDark(settings, tenantBranding) ? 'text-slate-200' : 'text-slate-500'}`}>
              Preview only — answers stay on this page. Use <b>Test &amp; record</b> to fill for real: catalogs, validation, submit, and a Test row on the pipeline.
            </p>
            {onTest ? (
              <Button size="sm" onClick={onTest} disabled={saving}>{saving ? 'Starting…' : 'Test & record'}</Button>
            ) : null}
          </div>
          <div className="mx-auto" style={{ maxWidth: formCardMaxWidth(settings), width: '100%' }}>
            {form?.facing === 'patron' ? (
              <FormTenantLogo
                settings={settings}
                branding={tenantBranding}
                darkPage={formPageBackgroundIsDark(settings, tenantBranding)}
              />
            ) : null}
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <FormBrandingHeader formName={form?.name} />
              <FormFillFields
                definition={{ pages }}
                settings={settings}
                locale={editLocale}
                answers={previewAnswers}
                onChange={(id, v) => setPreviewAnswers((prev) => ({ ...prev, [id]: v }))}
              />
              {form?.facing === 'patron' ? (
                <button
                  type="button"
                  className="mt-6 w-full rounded-lg border text-white font-semibold py-2.5 opacity-80"
                  style={formSubmitButtonStyle(settings, tenantBranding)}
                  disabled
                >
                  Submit
                </button>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function ToolBtn({ children, onClick, disabled, danger, label }) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={`h-6 w-6 rounded text-xs font-bold disabled:opacity-30 ${danger ? 'text-red-600 hover:bg-red-50' : 'text-slate-500 hover:bg-slate-100'}`}
    >
      {children}
    </button>
  )
}

function LocalizedField({ label, value, locale, onChange, multiline, rows = 2, placeholder, hint }) {
  return (
    <label className="block text-xs font-semibold text-slate-600">
      {label}
      {locale !== DEFAULT_LOCALE ? <span className="ml-1 font-normal text-slate-400">{localeName(locale)}</span> : null}
      {multiline ? (
        <textarea className={inputCls} rows={rows} placeholder={placeholder} value={labelOf(value, locale)} onChange={(e) => onChange(setLocalized(value, locale, e.target.value))} />
      ) : (
        <input className={inputCls} placeholder={placeholder} value={labelOf(value, locale)} onChange={(e) => onChange(setLocalized(value, locale, e.target.value))} />
      )}
      {hint ? <span className="mt-1 block font-normal text-slate-400">{hint}</span> : null}
    </label>
  )
}

function toDatetimeLocal(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 16)
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function fromDatetimeLocal(value) {
  if (!value) return ''
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? value : d.toISOString()
}

function FormProps({ form, settings, locale, onFormPatch, onSettingsChange }) {
  const locales = normalizeLocales(settings)
  const placements = normalizePlacements(settings)
  const appearance = normalizeAppearance(settings)
  const after = normalizeAfterSubmission(settings)
  const availability = normalizeAvailability(settings)
  const drafts = normalizeDrafts(settings)
  const addable = LOCALES.filter((l) => !locales.includes(l.code))
  const widthHint = FORM_WIDTHS.find((w) => w.v === appearance.width)?.hint
  const patchSettings = (fn) => onSettingsChange((s) => fn(s || {}))
  const setAppearance = (patch) => patchSettings((s) => ({ ...s, appearance: { ...normalizeAppearance(s), ...patch } }))
  const setAfter = (patch) => {
    const next = { ...normalizeAfterSubmission(settings), ...patch }
    patchSettings((s) => ({ ...s, afterSubmission: next, confirmMessage: next.confirmMessage }))
  }
  const setAvailability = (patch) => patchSettings((s) => ({ ...s, availability: { ...normalizeAvailability(s), ...patch } }))
  const setDrafts = (patch) => patchSettings((s) => ({ ...s, drafts: { ...normalizeDrafts(s), ...patch } }))
  const togglePlacement = (slot) => {
    const next = placements.includes(slot) ? placements.filter((p) => p !== slot) : [...placements, slot]
    patchSettings((s) => ({ ...s, placements: next }))
  }
  return (
    <div className="space-y-4 text-sm">
      <div className="text-xs font-semibold text-teal-800">Form · settings</div>
      <label className="block text-xs font-semibold text-slate-600">
        Name
        <input className={inputCls} value={form.name || ''} onChange={(e) => onFormPatch({ name: e.target.value })} />
      </label>
      <label className="block text-xs font-semibold text-slate-600">
        Slug
        <input className={inputCls} value={form.slug || ''} onChange={(e) => onFormPatch({ slug: e.target.value })} />
      </label>

      <div className="border-t border-slate-200 pt-3">
        <div className="text-xs font-semibold text-slate-800 mb-2">Appearance</div>
        <div className="text-xs font-semibold text-slate-600 mb-1">Background color</div>
        <div className="flex items-center gap-2">
          <input
            type="color"
            className="h-9 w-10 cursor-pointer rounded border border-slate-300 bg-white p-0.5"
            value={/^#[0-9a-fA-F]{6}$/.test(appearance.bgColor) ? appearance.bgColor : '#F2F4F3'}
            onChange={(e) => setAppearance({ bgColor: e.target.value })}
            aria-label="Background color"
          />
          <input className={inputCls + ' mt-0'} value={appearance.bgColor} onChange={(e) => setAppearance({ bgColor: e.target.value })} placeholder="#F2F4F3" />
        </div>
        <div className="mt-2 flex gap-1.5">
          {BG_SWATCHES.map((c) => (
            <button
              key={c}
              type="button"
              aria-label={`Background ${c}`}
              className={`h-6 w-6 rounded-full border ${appearance.bgColor.toLowerCase() === c.toLowerCase() ? 'border-teal-700 ring-2 ring-teal-600/30' : 'border-slate-300'}`}
              style={{ background: c }}
              onClick={() => setAppearance({ bgColor: c })}
            />
          ))}
        </div>
        <label className="block text-xs font-semibold text-slate-600 mt-3">
          Background image URL <span className="font-normal text-slate-400">(covers the page; empty = solid color)</span>
          <input className={inputCls} value={appearance.bgImage} placeholder="https://…/banner.jpg" onChange={(e) => setAppearance({ bgImage: e.target.value })} />
        </label>
        <div className="mt-3">
          <div className="text-xs font-semibold text-slate-600 mb-1">Form width</div>
          <div className="grid grid-cols-2 gap-1">
            {FORM_WIDTHS.map((w) => (
              <button
                key={w.v}
                type="button"
                className={`rounded-lg border px-2 py-1.5 text-xs font-semibold ${appearance.width === w.v ? chipOn : chipOff}`}
                onClick={() => setAppearance({ width: w.v })}
              >
                {w.label}
              </button>
            ))}
          </div>
          {appearance.width === 'custom' ? (
            <div className="mt-2 flex gap-1">
              <input
                className={inputCls + ' mt-0'}
                inputMode="numeric"
                value={appearance.customValue}
                onChange={(e) => {
                  const n = parseInt(e.target.value, 10)
                  setAppearance({ customValue: Number.isNaN(n) ? '' : n })
                }}
              />
              <select className={inputCls + ' mt-0 w-20'} value={appearance.customUnit} onChange={(e) => setAppearance({ customUnit: e.target.value })}>
                <option value="px">px</option>
                <option value="%">%</option>
              </select>
            </div>
          ) : null}
          {widthHint ? <p className="mt-1 text-xs text-slate-500">{widthHint}</p> : null}
        </div>
        {form.facing === 'patron' ? (
          <div className="mt-3 space-y-2 border-t border-slate-100 pt-3">
            <div className="text-xs font-semibold text-slate-600">Tenant branding</div>
            <label className="flex items-center gap-2 text-xs text-slate-700">
              <input
                type="checkbox"
                className="rounded border-slate-300"
                checked={appearance.showTenantLogo}
                onChange={(e) => setAppearance({ showTenantLogo: e.target.checked })}
              />
              Show tenant logo above the form card
            </label>
            <label className="flex items-center gap-2 text-xs text-slate-700">
              <input
                type="checkbox"
                className="rounded border-slate-300"
                checked={appearance.useClubColors}
                onChange={(e) => setAppearance({ useClubColors: e.target.checked })}
              />
              Use tenant theme colours (page tint and submit button)
            </label>
            <p className="text-xs text-slate-500">
              Logo sits outside the form card; the title stays inside. Colours come from platform tenant settings. Manual background colour and image still apply when set.
            </p>
          </div>
        ) : null}
        <p className="mt-2 text-xs text-slate-500">Appearance applies in Preview and when respondents fill the form. The builder canvas stays neutral.</p>
      </div>

      <div className="border-t border-slate-200 pt-3">
        <div className="text-xs font-semibold text-slate-800 mb-2">Audience &amp; access</div>
        <div className="flex gap-1">
          {[['internal', 'Internal'], ['patron', 'Patron']].map(([v, l]) => (
            <button key={v} type="button" className={`flex-1 rounded-lg border px-2 py-1.5 text-xs font-semibold ${form.facing === v ? chipOn : chipOff}`} onClick={() => onFormPatch({ facing: v })}>
              {l}
            </button>
          ))}
        </div>
        {form.facing === 'patron' ? (
          <>
            <p className="mt-2 text-xs text-slate-500">Invite links — unique token per recipient. Strongest control without login: sent → opened → started → submitted, one response per invite.</p>
            <p className="mt-1 text-xs text-slate-400">Open link, access code, and verified email are not offered in v1.</p>
          </>
        ) : (
          <p className="mt-2 text-xs text-slate-500">Staff sign-in required. Identity is bound to the submission; user attributes are available for conditions and prefill.</p>
        )}
      </div>

      <div className="border-t border-slate-200 pt-3">
        <div className="text-xs font-semibold text-slate-800 mb-1">Languages</div>
        <p className="text-xs text-slate-500 mb-2">Form content only. English is always on. Use the page language switcher to translate titles and labels.</p>
        <div className="flex flex-wrap gap-1 mb-2">
          {locales.map((code) => (
            <span key={code} className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[11px] font-semibold text-slate-700">
              {localeName(code)}
              {code !== DEFAULT_LOCALE ? (
                <button
                  type="button"
                  className="text-slate-400 hover:text-red-600"
                  aria-label={`Remove ${localeName(code)}`}
                  onClick={() => patchSettings((s) => ({ ...s, locales: locales.filter((c) => c !== code) }))}
                >
                  ×
                </button>
              ) : null}
            </span>
          ))}
        </div>
        {addable.length ? (
          <select
            className={inputCls}
            value=""
            onChange={(e) => {
              if (!e.target.value) return
              patchSettings((s) => ({ ...s, locales: [...locales, e.target.value] }))
            }}
          >
            <option value="">Add language…</option>
            {addable.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
          </select>
        ) : null}
      </div>

      <div className="border-t border-slate-200 pt-3">
        <div className="text-xs font-semibold text-slate-800 mb-1">Show in</div>
        <p className="text-xs text-slate-500 mb-2">Published forms always appear in the Forms catalog. Tick a slot to launch from that record.</p>
        <div className="space-y-1.5">
          {PLACEMENT_SLOTS.map((slot) => (
            <label key={slot.value} className="flex items-start gap-2 text-xs text-slate-800">
              <input type="checkbox" className="mt-0.5" checked={placements.includes(slot.value)} onChange={() => togglePlacement(slot.value)} />
              {slot.label}
            </label>
          ))}
        </div>
      </div>

      <div className="border-t border-slate-200 pt-3">
        <div className="text-xs font-semibold text-slate-800 mb-2">Availability</div>
        <label className="block text-xs font-semibold text-slate-600">
          Timezone <span className="font-normal text-slate-400">(governs date variables and the window below)</span>
          <select
            className={inputCls}
            value={TIMEZONES.includes(settings.timezone) ? settings.timezone : (settings.timezone || 'Australia/Sydney')}
            onChange={(e) => patchSettings((s) => ({ ...s, timezone: e.target.value }))}
          >
            {!TIMEZONES.includes(settings.timezone) && settings.timezone ? (
              <option value={settings.timezone}>{settings.timezone}</option>
            ) : null}
            {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
          </select>
        </label>
        <label className="block text-xs font-semibold text-slate-600 mt-2">
          Opens
          <input className={inputCls} type="datetime-local" value={toDatetimeLocal(availability.opensAt)} onChange={(e) => setAvailability({ opensAt: fromDatetimeLocal(e.target.value) })} />
        </label>
        <label className="block text-xs font-semibold text-slate-600 mt-2">
          Closes
          <input className={inputCls} type="datetime-local" value={toDatetimeLocal(availability.closesAt)} onChange={(e) => setAvailability({ closesAt: fromDatetimeLocal(e.target.value) })} />
        </label>
        <label className="block text-xs font-semibold text-slate-600 mt-2">
          Max submissions <span className="font-normal text-slate-400">(blank = unlimited; test runs do not count)</span>
          <input
            className={inputCls}
            inputMode="numeric"
            value={availability.maxSubmissions}
            onChange={(e) => setAvailability({ maxSubmissions: e.target.value.replace(/[^\d]/g, '') })}
          />
        </label>
        <LocalizedField
          label="Closed message"
          value={availability.closedMessage}
          locale={locale}
          multiline
          rows={2}
          placeholder="This form is closed."
          onChange={(closedMessage) => setAvailability({ closedMessage })}
        />
      </div>

      <div className="border-t border-slate-200 pt-3">
        <div className="text-xs font-semibold text-slate-800 mb-2">After submission</div>
        <div className="flex gap-1">
          {[['message', 'Confirmation'], ['redirect', 'Redirect']].map(([v, l]) => (
            <button key={v} type="button" className={`flex-1 rounded-lg border px-2 py-1.5 text-xs font-semibold ${after.action === v ? chipOn : chipOff}`} onClick={() => setAfter({ action: v })}>
              {l}
            </button>
          ))}
        </div>
        {after.action === 'message' ? (
          <>
            <LocalizedField
              label="Confirmation message"
              value={after.confirmMessage}
              locale={locale}
              multiline
              rows={3}
              onChange={(confirmMessage) => setAfter({ confirmMessage })}
            />
            <p className="text-xs text-slate-500">
              Supports variables: <code className="text-[11px]">{'{{field.first_name}}'}</code>, <code className="text-[11px]">{'{{submission_id}}'}</code>, <code className="text-[11px]">{'{{current_year}}'}</code>, <code className="text-[11px]">{'{{form_name}}'}</code>
            </p>
            <label className="mt-2 flex items-start gap-2 text-xs text-slate-800">
              <input type="checkbox" className="mt-0.5" checked={after.allowResubmit} onChange={(e) => setAfter({ allowResubmit: e.target.checked })} />
              Allow “Submit another response” (new assignment / invite — not the same token)
            </label>
          </>
        ) : (
          <>
            <label className="block text-xs font-semibold text-slate-600 mt-2">
              Redirect URL <span className="font-normal text-slate-400">(https only)</span>
              <input className={inputCls} value={after.redirectUrl} placeholder="https://intranet.example.com/thanks" onChange={(e) => setAfter({ redirectUrl: e.target.value })} />
            </label>
            <label className="mt-2 flex items-start gap-2 text-xs text-slate-800">
              <input type="checkbox" className="mt-0.5" checked={after.appendRef} onChange={(e) => setAfter({ appendRef: e.target.checked })} />
              Append reference (<code className="text-[11px]">?ref=…</code>)
            </label>
            <p className="mt-1 text-xs text-slate-500">The reference number is still shown briefly before redirecting, so the respondent always gets their receipt.</p>
          </>
        )}
      </div>

      <div className="border-t border-slate-200 pt-3">
        <div className="text-xs font-semibold text-slate-800 mb-2">Drafts</div>
        <div className="text-xs font-semibold text-slate-600 mb-1">When a new version is published</div>
        <div className="flex gap-1">
          {[['pin', 'Pin'], ['invalidate', 'Invalidate']].map(([v, l]) => (
            <button key={v} type="button" className={`flex-1 rounded-lg border px-2 py-1.5 text-xs font-semibold ${drafts.onNewVersion === v ? chipOn : chipOff}`} onClick={() => setDrafts({ onNewVersion: v })}>
              {l}
            </button>
          ))}
        </div>
        <p className="mt-1 text-xs text-slate-500">
          {drafts.onNewVersion === 'invalidate'
            ? 'Safer for compliance wording changes — unfinished drafts are discarded with a message.'
            : 'Draft stays on the old version until submitted. Default for general forms.'}
        </p>
        <label className="block text-xs font-semibold text-slate-600 mt-2">
          Retention (days)
          <input
            className={inputCls}
            inputMode="numeric"
            value={drafts.retentionDays}
            onChange={(e) => {
              const n = parseInt(e.target.value, 10)
              setDrafts({ retentionDays: Number.isNaN(n) ? '' : n })
            }}
          />
        </label>
        <p className="mt-1 text-xs text-slate-400">Stored on the form. Pin / invalidate and expiry are applied when the draft engine reads this setting.</p>
      </div>
    </div>
  )
}

function PageProps({ page, pages, locale, update, remove, move }) {
  const i = pages.findIndex((p) => p.id === page.id)
  return (
    <div className="space-y-3 text-sm">
      <div className="text-xs font-semibold text-teal-800">Page settings</div>
      <LocalizedField label="Title" value={page.title} locale={locale} onChange={(title) => update(page.id, { title })} />
      <LocalizedField label="Description" value={page.description} locale={locale} multiline onChange={(description) => update(page.id, { description })} />
      <ConditionEditor value={page.condition} pages={pages} selfId={null} onChange={(condition) => update(page.id, { condition })} />
      <div className="flex gap-1">
        <Button variant="outline" size="sm" disabled={i <= 0} onClick={() => move(page.id, -1)}>Earlier</Button>
        <Button variant="outline" size="sm" disabled={i === pages.length - 1} onClick={() => move(page.id, 1)}>Later</Button>
        <Button variant="destructive" size="sm" disabled={pages.length <= 1} onClick={() => remove(page.id)}>Remove page</Button>
      </div>
    </div>
  )
}

function SectionProps({ section, pages, locale, update }) {
  return (
    <div className="space-y-3 text-sm">
      <div className="text-xs font-semibold text-teal-800">Section</div>
      <LocalizedField
        label="Title"
        hint="Optional — leave blank for layout only"
        value={section.label}
        locale={locale}
        placeholder="Untitled — layout only"
        onChange={(label) => update(section.id, { label: labelOf(label, locale) ? label : null })}
      />
      <LocalizedField label="Description" value={section.description} locale={locale} multiline onChange={(description) => update(section.id, { description })} />
      <div>
        <div className="text-xs font-semibold text-slate-600 mb-1">Columns</div>
        <div className="flex gap-1">
          {[1, 2, 3].map((n) => (
            <button key={n} type="button" className={`flex-1 rounded-lg border px-2 py-1.5 text-xs font-semibold ${(section.columns || 1) === n ? chipOn : chipOff}`} onClick={() => update(section.id, { columns: n })}>
              {n}
            </button>
          ))}
        </div>
      </div>
      <ConditionEditor value={section.condition} pages={pages} selfId={null} onChange={(condition) => update(section.id, { condition })} />
    </div>
  )
}

function FieldProps({ field, pages, locale, update }) {
  const choice = CHOICE_TYPES.includes(field.type)
  return (
    <div className="space-y-3 text-sm">
      <div className="text-xs font-semibold text-teal-800">Field · {typeName(field.type)}</div>
      <LocalizedField label="Label" value={field.label} locale={locale} onChange={(label) => update(field.id, { label })} />
      <LocalizedField label="Placeholder" value={field.placeholder} locale={locale} onChange={(placeholder) => update(field.id, { placeholder })} />
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={!!field.required} onChange={(e) => update(field.id, { required: e.target.checked })} />
        Required
      </label>
      {!ALWAYS_FULL.includes(field.type) ? (
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={field.span === 'full'} onChange={(e) => update(field.id, { span: e.target.checked ? 'full' : undefined })} />
          Full row
        </label>
      ) : null}
      {choice ? (
        <>
          <label className="block text-xs font-semibold text-slate-600">
            Options source
            <select className={inputCls} value={field.catalog || ''} onChange={(e) => update(field.id, { catalog: e.target.value || undefined })}>
              {CATALOGS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </label>
          {!field.catalog ? (
            <label className="block text-xs font-semibold text-slate-600">
              Options (one per line)
              {locale !== DEFAULT_LOCALE ? <span className="ml-1 font-normal text-slate-400">{localeName(locale)}</span> : null}
              <textarea
                className={inputCls}
                rows={5}
                value={(field.options || []).map((o) => optionLine(o, locale)).join('\n')}
                onChange={(e) => update(field.id, { options: parseOptionLines(e.target.value, field.options, locale) })}
              />
            </label>
          ) : (
            <p className="text-xs text-slate-500">Values come from the platform catalog at fill time. The stored answer is the stable id.</p>
          )}
        </>
      ) : null}
      {field.type === 'matrix' ? (
        <>
          <label className="block text-xs font-semibold text-slate-600">
            Rows (one per line)
            {locale !== DEFAULT_LOCALE ? <span className="ml-1 font-normal text-slate-400">{localeName(locale)}</span> : null}
            <textarea className={inputCls} rows={3} value={(field.rows || []).map((o) => optionLine(o, locale)).join('\n')} onChange={(e) => update(field.id, { rows: parseOptionLines(e.target.value, field.rows, locale) })} />
          </label>
          <label className="block text-xs font-semibold text-slate-600">
            Scale (one per line)
            {locale !== DEFAULT_LOCALE ? <span className="ml-1 font-normal text-slate-400">{localeName(locale)}</span> : null}
            <textarea className={inputCls} rows={3} value={(field.options || []).map((o) => optionLine(o, locale)).join('\n')} onChange={(e) => update(field.id, { options: parseOptionLines(e.target.value, field.options, locale) })} />
          </label>
        </>
      ) : null}
      <ConditionEditor value={field.condition} pages={pages} selfId={field.id} onChange={(condition) => update(field.id, { condition })} />
    </div>
  )
}

function ConditionEditor({ value, pages, selfId, onChange, label = 'Show when' }) {
  const sources = useMemo(
    () => flattenFields(pages).filter((f) => f.id !== selfId && !NO_CONDITION_SOURCE.includes(f.type)),
    [pages, selfId]
  )
  const src = sources.find((f) => f.id === value?.fieldId)
  const ops = opsForSource(src)
  const on = !!value?.fieldId

  return (
    <div>
      <div className="text-xs font-semibold text-slate-600 mb-1">{label}</div>
      <label className="flex items-center gap-2 text-sm mb-2">
        <input
          type="checkbox"
          checked={on}
          onChange={(e) => onChange(e.target.checked ? { fieldId: sources[0]?.id, operator: 'equals', value: '' } : undefined)}
        />
        Conditional
      </label>
      {on ? (
        <div className="space-y-2">
          <select className={inputCls} value={value.fieldId || ''} onChange={(e) => onChange({ ...value, fieldId: e.target.value })}>
            {sources.map((f) => <option key={f.id} value={f.id}>{labelOf(f.label) || f.id}</option>)}
          </select>
          <select className={inputCls} value={value.operator || 'equals'} onChange={(e) => onChange({ ...value, operator: e.target.value })}>
            {ops.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
          </select>
          {operatorNeedsValue(value.operator || 'equals') ? (
            src && CHOICE_TYPES.includes(src.type) && !src.catalog ? (
              <select className={inputCls} value={value.value || ''} onChange={(e) => onChange({ ...value, value: e.target.value })}>
                <option value="">Select…</option>
                {(src.options || []).map((o) => {
                  const v = typeof o === 'string' ? o : o.value
                  return <option key={v} value={v}>{typeof o === 'string' ? o : (o.label || o.value)}</option>
                })}
              </select>
            ) : (
              <input className={inputCls} value={value.value || ''} onChange={(e) => onChange({ ...value, value: e.target.value })} placeholder="Value" />
            )
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

function WorkflowPanel({ pages, settings, onSettingsChange }) {
  const enabled = !!settings.workflow?.enabled
  const steps = useMemo(
    () => (settings.workflow?.steps || []).map(normalizeWorkflowStep).filter(Boolean),
    [settings.workflow?.steps]
  )
  const [users, setUsers] = useState([])
  const [roles, setRoles] = useState([])

  useEffect(() => {
    if (!enabled) return undefined
    let cancelled = false
    authFetch(getLocalApiUrl('/api/forms/catalogs/users')).then((r) => r.json()).then((j) => {
      if (!cancelled) setUsers(j.data || [])
    }).catch(() => {})
    authFetch(getLocalApiUrl('/api/forms/catalogs/roles')).then((r) => r.json()).then((j) => {
      if (!cancelled) setRoles(j.data || [])
    }).catch(() => {})
    return () => { cancelled = true }
  }, [enabled])

  const setWorkflow = (patch) => onSettingsChange((s) => ({
    ...s,
    workflow: { enabled: false, steps: [], ...(s.workflow || {}), ...patch },
  }))
  const setSteps = (next) => setWorkflow({ enabled: true, steps: next })
  const setStep = (id, patch) => setSteps(steps.map((s) => (s.id === id ? { ...s, ...patch } : s)))

  const allSections = pages.flatMap((p, pi) => (p.sections || []).map((s) => ({
    ...s,
    pageLabel: pageTitle(p, pi),
  })))

  const toggle = (on) => setWorkflow({
    enabled: on,
    steps: on && steps.length === 0 ? defaultWorkflowSteps(pages) : steps,
  })

  const addStep = (type) => setSteps([...steps, {
    id: uid('w'),
    type,
    name: type === 'fill' ? 'New fill step' : 'New approval step',
    assignee: { type: type === 'fill' ? 'user' : 'role', id: '', label: '' },
    sections: type === 'fill' ? [] : undefined,
  }])

  const removeStep = (id) => {
    if (steps.length <= 1) return
    setSteps(steps.filter((s) => s.id !== id))
  }

  const moveStep = (id, dir) => {
    const i = steps.findIndex((s) => s.id === id)
    const j = i + dir
    if (j < 0 || j >= steps.length) return
    const next = [...steps]
    ;[next[i], next[j]] = [next[j], next[i]]
    setSteps(next)
  }

  const setAssigneeType = (step, type) => {
    setStep(step.id, { assignee: { type, id: '', label: '' } })
  }

  const setAssigneePrincipal = (step, type, id, catalog) => {
    const row = catalog.find((c) => String(c.value) === String(id))
    setStep(step.id, { assignee: { type, id, label: row?.label || '' } })
  }

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-4">
      <label className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3">
        <input
          type="checkbox"
          className="mt-1"
          checked={enabled}
          onChange={(e) => toggle(e.target.checked)}
        />
        <div>
          <div className="text-sm font-semibold text-slate-800">Enable workflow for this form</div>
          <p className="text-sm text-slate-500 mt-1">
            Off: the form submits directly (single respondent). On: the submission travels through the steps below —
            each fill step hands its sections to a different person, approval steps review and decide.
            Assignees are existing application users and roles.
          </p>
        </div>
      </label>

      {enabled ? (
        <>
          <div className="space-y-0">
            {steps.map((st, i) => (
              <div key={st.id} className="grid grid-cols-[40px_1fr] gap-2.5">
                <div className="flex flex-col items-center">
                  <span className={`flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full text-xs font-bold text-white ${st.type === 'approve' ? 'bg-violet-600' : 'bg-teal-700'}`}>
                    {i + 1}
                  </span>
                  {i < steps.length - 1 ? <span className="mt-1 w-0.5 flex-1 bg-slate-200" /> : null}
                </div>
                <div className="mb-3 rounded-xl border border-slate-200 bg-white p-4 space-y-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`rounded-md px-2 py-0.5 text-xs font-bold ${st.type === 'approve' ? 'bg-violet-50 text-violet-700' : 'bg-teal-50 text-teal-800'}`}>
                      {st.type === 'fill' ? '✎ Fill' : '✓ Approve'}
                    </span>
                    <input
                      className="min-w-[160px] flex-1 rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm font-semibold text-slate-900 outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-600/20"
                      value={st.name}
                      onChange={(e) => setStep(st.id, { name: e.target.value })}
                    />
                    <div className="flex items-center gap-0.5">
                      <ToolBtn label="Move step up" onClick={() => moveStep(st.id, -1)} disabled={i === 0}>↑</ToolBtn>
                      <ToolBtn label="Move step down" onClick={() => moveStep(st.id, 1)} disabled={i === steps.length - 1}>↓</ToolBtn>
                      <ToolBtn label="Delete step" danger onClick={() => removeStep(st.id)} disabled={steps.length <= 1}>✕</ToolBtn>
                    </div>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <label className="block text-xs font-semibold text-slate-600">
                      Assigned to
                      <select
                        className={inputCls}
                        value={st.assignee?.type || 'initiator'}
                        onChange={(e) => setAssigneeType(st, e.target.value)}
                      >
                        {WORKFLOW_ASSIGNEE_TYPES.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </label>
                    {st.assignee?.type === 'user' ? (
                      <label className="block text-xs font-semibold text-slate-600">
                        User
                        <select
                          className={inputCls}
                          value={st.assignee.id || ''}
                          onChange={(e) => setAssigneePrincipal(st, 'user', e.target.value, users)}
                        >
                          <option value="">Select user…</option>
                          {users.map((u) => (
                            <option key={u.value} value={u.value}>{u.label}</option>
                          ))}
                        </select>
                      </label>
                    ) : null}
                    {st.assignee?.type === 'role' ? (
                      <label className="block text-xs font-semibold text-slate-600">
                        Role
                        <select
                          className={inputCls}
                          value={st.assignee.id || ''}
                          onChange={(e) => setAssigneePrincipal(st, 'role', e.target.value, roles)}
                        >
                          <option value="">Select role…</option>
                          {roles.map((r) => (
                            <option key={r.value} value={r.value}>{r.label}</option>
                          ))}
                        </select>
                      </label>
                    ) : null}
                  </div>

                  {st.type === 'fill' ? (
                    <div>
                      <div className="text-xs font-semibold text-slate-600">
                        Sections this step can fill
                        <span className="ml-1 font-normal text-slate-400">(earlier steps&apos; sections show read-only)</span>
                      </div>
                      <div className="mt-2 flex flex-col gap-1.5">
                        {allSections.length === 0 ? (
                          <p className="text-xs text-slate-400">Add sections on the Build tab first.</p>
                        ) : allSections.map((s) => {
                          const on = (st.sections || []).includes(s.id)
                          return (
                            <label key={s.id} className={`flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm ${on ? 'border-teal-600 bg-teal-50 text-teal-900' : 'border-slate-200 bg-white text-slate-700'}`}>
                              <input
                                type="checkbox"
                                checked={on}
                                onChange={() => setStep(st.id, {
                                  sections: on
                                    ? (st.sections || []).filter((x) => x !== s.id)
                                    : [...(st.sections || []), s.id],
                                })}
                              />
                              {s.pageLabel} · {labelOf(s.label) || 'Untitled'}
                            </label>
                          )
                        })}
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500 m-0">
                      Reviewer sees all answers so far and can <b>Approve</b>, <b>Return</b> to the previous fill step (with a comment), or <b>Reject</b>.
                    </p>
                  )}

                  <div className="border-t border-slate-100 pt-3">
                    <ConditionEditor
                      value={st.condition}
                      pages={pages}
                      onChange={(condition) => setStep(st.id, { condition })}
                      label="Run this step only when…"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-2 pl-[50px]">
            <Button type="button" variant="outline" onClick={() => addStep('fill')}>+ Fill step</Button>
            <Button type="button" variant="outline" onClick={() => addStep('approve')}>+ Approval step</Button>
          </div>
        </>
      ) : null}
    </div>
  )
}
